import { useState, useMemo } from 'react'
import { validatePassword } from '../../../shared/lib/passwordValidation'

function ForcePasswordChange({ onComplete, onSignOut }) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const validation = useMemo(() => validatePassword(password), [password])
  const passwordsMatch = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword
  const allValid = validation.valid && passwordsMatch

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!validation.valid) {
      setError('Please fix the password requirements below')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      await onComplete(password)
    } catch (err) {
      setError(err.message || 'Failed to update password')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#012438' }}>
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#FAFAFF' }}>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ backgroundColor: '#8AD0D6', opacity: 0.08 }} />
        <div className="absolute -bottom-40 -left-40 rounded-full" style={{ width: '500px', height: '500px', backgroundColor: '#012438', opacity: 0.04 }} />
        <div className="absolute top-1/3 right-8 w-72 h-72 rounded-full" style={{ backgroundColor: '#BCC5CD', opacity: 0.12 }} />
        <div className="absolute top-16 left-1/3 w-48 h-48 rounded-full" style={{ backgroundColor: '#8AD0D6', opacity: 0.05 }} />
        <div className="absolute right-0 top-0 bottom-0 w-px" style={{ backgroundColor: '#BCC5CD' }} />

        <div className="relative z-10 flex flex-col justify-center px-16">
          <img src="/logo.png" alt="SSA Logo" className="w-16 h-16 rounded-2xl object-contain mb-8" />
          <h1 className="text-4xl font-bold leading-tight mb-3" style={{ color: '#012438' }}>
            Sidewalk Security Advisors
          </h1>
          <div className="w-12 h-1 rounded-full mb-5" style={{ backgroundColor: '#8AD0D6' }} />
          <p className="text-lg font-medium" style={{ color: '#3F5A6A' }}>
            Executive Advisory Platform
          </p>
        </div>

        <div className="absolute bottom-8 left-16">
          <p className="text-xs tracking-widest uppercase font-medium" style={{ color: '#7E8F9C' }}>
            Secure &middot; Strategic &middot; Advisory
          </p>
        </div>
      </div>

      {/* Right panel - password change form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12" style={{ backgroundColor: '#012438' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo.png" alt="SSA Logo" className="w-12 h-12 rounded-xl object-contain mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white">Sidewalk Security Advisors</h1>
            <p className="text-sm mt-1" style={{ color: '#7E8F9C' }}>Executive Advisory Platform</p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold" style={{ backgroundColor: '#8AD0D6', color: '#012438' }}>1</span>
            <div className="flex-1 h-px" style={{ backgroundColor: '#3F5A6A' }} />
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold" style={{ backgroundColor: '#3F5A6A', color: '#7E8F9C' }}>2</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white">
              Set Your Password
            </h2>
            <p className="mt-2 text-sm" style={{ color: '#7E8F9C' }}>
              Welcome! Please create a password for your account to continue.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="flex items-start gap-3 border px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#fca5a5' }}>
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="new-password" className="block text-sm font-medium mb-1.5" style={{ color: '#BCC5CD' }}>
                New Password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full px-3.5 py-2.5 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors"
                style={{ backgroundColor: '#213C4E', borderColor: '#3F5A6A', border: '1px solid #3F5A6A' }}
                placeholder="Minimum 14 characters"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium mb-1.5" style={{ color: '#BCC5CD' }}>
                Confirm New Password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full px-3.5 py-2.5 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-colors"
                style={{ backgroundColor: '#213C4E', borderColor: '#3F5A6A', border: '1px solid #3F5A6A' }}
                placeholder="Re-enter your new password"
              />
            </div>

            {/* Real-time password requirements checklist */}
            <div className="text-xs space-y-1" style={{ color: '#7E8F9C' }}>
              <p className="font-medium mb-1.5">Password requirements:</p>
              {validation.checks.filter(c => c.id !== 'maxLength').map(check => (
                <div key={check.id} className="flex items-center gap-2">
                  {password.length === 0 ? (
                    <div className="w-3.5 h-3.5 rounded-full border" style={{ borderColor: '#3F5A6A' }} />
                  ) : check.passed ? (
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="#4ade80" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="#f87171" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span style={{ color: password.length === 0 ? '#7E8F9C' : check.passed ? '#4ade80' : '#f87171' }}>
                    {check.label}
                  </span>
                </div>
              ))}
              {/* Passwords match check */}
              <div className="flex items-center gap-2">
                {confirmPassword.length === 0 ? (
                  <div className="w-3.5 h-3.5 rounded-full border" style={{ borderColor: '#3F5A6A' }} />
                ) : passwordsMatch ? (
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="#4ade80" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="#f87171" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <span style={{ color: confirmPassword.length === 0 ? '#7E8F9C' : passwordsMatch ? '#4ade80' : '#f87171' }}>
                  Passwords match
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !allValid}
              className="w-full flex items-center justify-center py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#8AD0D6', color: '#012438' }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#a8eff4')}
              onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#8AD0D6')}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Updating password...
                </>
              ) : 'Continue'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={onSignOut}
              className="text-sm font-medium transition-colors"
              style={{ color: '#7E8F9C' }}
              onMouseEnter={(e) => e.target.style.color = '#BCC5CD'}
              onMouseLeave={(e) => e.target.style.color = '#7E8F9C'}
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForcePasswordChange
